import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { e53Sample } from './types';

// Interface for creating a new sample
export interface CreateE53SampleRequest extends Partial<e53Sample> {
  fileId: number; // Required field for new sample creation
}

/**
 * Hook for adding a new e53 sample
 * 
 * @returns Mutation for creating a new e53 sample
 */
export const useAddE53Sample = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (newSample: CreateE53SampleRequest) => {
      const { fileId, ...rest } = newSample;
      const payload = {
        ...rest,
        e53FileId: fileId,
      };
      
      const response = await axios.post('/data-sample/e53', payload);
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate relevant queries to refetch the updated data
      queryClient.invalidateQueries({ 
        queryKey: ['e53Samples', variables.fileId] 
      });
    },
  });
};

/**
 * Hook for updating an existing e53 sample
 * 
 * @returns Mutation for updating an e53 sample
 */
export const useUpdateE53Sample = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (updatedSample: e53Sample) => {
      const { fileId, ...rest } = updatedSample as any;
      const payload = {
        ...rest,
        e53FileId: fileId,
      };
      
      const response = await axios.put('/data-sample/e53', payload);
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate relevant queries to refetch the updated data
      queryClient.invalidateQueries({ 
        queryKey: ['e53Samples'] 
      });
    },
  });
};

/**
 * Hook for deleting an e53 sample or amendment
 * 
 * @returns Mutation for deleting an e53 sample
 */
export const useDeleteE53Sample = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ sampleId, fileId }: { sampleId: number; fileId: number }) => {
      // Align with ENO1 API: pass sampleId and dataSetId as query params
      const response = await axios.delete(`/data-sample/e53`, {
        params: {
          sampleId,
          dataSetId: fileId,
        },
      });
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate relevant queries to refetch the updated data
      queryClient.invalidateQueries({ 
        queryKey: ['e53Samples', variables.fileId] 
      });
    },
  });
}; 